import React, { useMemo } from 'react';
import { EventData, Joyride, STATUS, Step } from 'react-joyride';
import { HelpCircle } from 'lucide-react';

/**
 * 화면 안내(투어). `GRK_project` 의 것을 그대로 가져왔다 — 두 도구가 같은 손에 익게.
 *
 * 화면은 `TourStep[]` 만 선언하면 된다. 딤·말풍선 위치·스크롤 따라가기는
 * react-joyride 가 맡는다.
 *
 * ⚠️ react-joyride 3.x 는 인터넷에 도는 2.x 예제와 API 가 다르다.
 *    default export 가 없고(`{ Joyride }`), `callback` 이 아니라 `onEvent`,
 *    `showProgress`·`showSkipButton` 같은 표시 옵션은 `options` 안에 들어간다.
 *    3.2.0 의 `dist/index.d.cts` 를 읽고 맞춘 것이다.
 *
 * `onEnter` 는 그 단계에서 화면이 어떤 상태여야 하는지를 **전부** 적는다.
 * 그래야 「이전」으로 돌아갈 때도 앞 단계가 열어둔 것이 저절로 닫힌다.
 *
 * ⚠️ `onEnter` 는 joyride 의 `before` 훅으로 넘긴다. `step:before` 이벤트에서
 *    처리하면 팝업처럼 **그 단계에서 비로소 생기는 대상**을 짚을 수 없다 —
 *    joyride 는 대상을 찾은 뒤에야 `step:before` 를 쏘므로, 팝업을 여는 쪽과
 *    기다리는 쪽이 서로를 기다리다 멈춘다(2026-09-20 실측). `before` 는 대상을
 *    찾기 전에 돌고, 이어서 `targetWaitTimeout`(기본 1초) 동안 기다려 준다.
 * 단계를 이름(`name`)으로 부르는 이유는, 번호로 잡으면 중간에 하나
 * 끼워 넣을 때 다른 단계의 동작이 조용히 어긋나기 때문이다.
 */
export type TourStep = Step & {
  name: string;
  onEnter?: () => void;
};

interface Props {
  steps: TourStep[];
  run: boolean;
  onFinish: () => void;
}

export const Tour: React.FC<Props> = ({ steps, run, onFinish }) => {
  const joyrideSteps = useMemo(
    () => steps.map((step) => ({ ...step, before: async () => { step.onEnter?.(); } })),
    [steps],
  );

  const handleEvent = ({ status }: EventData) => {
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onFinish();
    }
  };

  return (
    <Joyride
      steps={joyrideSteps}
      run={run}
      continuous
      onEvent={handleEvent}
      options={{
        showProgress: true,
        // v3 기본값은 ['back','close','primary'] 라 건너뛰기가 없다
        buttons: ['skip', 'back', 'primary'],
        skipBeacon: true,
        overlayClickAction: false,
        scrollOffset: 80,
        primaryColor: '#4f46e5',
        textColor: '#334155',
        overlayColor: 'rgba(15, 23, 42, 0.62)',
        spotlightRadius: 8,
        width: 300,
        // 팝업(z-50)보다 위에 있어야 팝업 단계에서 딤이 제대로 깔린다
        zIndex: 10000,
      }}
      locale={{
        back: '이전',
        next: '다음',
        // showProgress 를 켜면 next 가 아니라 이쪽이 쓰인다
        nextWithProgress: '다음 ({current}/{total})',
        last: '끝',
        skip: '건너뛰기',
        close: '닫기',
        open: '안내 열기',
      }}
      styles={{
        tooltip: { borderRadius: 12, padding: 16, fontSize: 13 },
        tooltipTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
        tooltipContent: { lineHeight: 1.65 },
        buttonPrimary: { borderRadius: 7, padding: '7px 14px', fontSize: 13, fontWeight: 500 },
        buttonBack: { color: '#64748b', fontSize: 13 },
        buttonSkip: { color: '#94a3b8', fontSize: 12 },
      }}
    />
  );
};

/** 화면 제목 옆에 붙는 물음표 */
export const TourButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="이 화면 안내"
    title="이 화면 안내"
    className="text-slate-300 hover:text-indigo-600 transition-colors align-middle"
  >
    <HelpCircle size={15} />
  </button>
);
